import { Dispatch, ReactNode, SetStateAction, useState } from "react";

import BulkWipItemsActionForm from "../components/forms/wips/BulkWipItemsActionForm";
import { makePostApiCall } from "../shared/makeApiCall";

interface ReasonOption {
  value: number;
  label?: string;
}

interface SelectedWipItem {
  barcode: string;
}

interface ModalState {
  open: boolean;
  childComponent: ReactNode;
}

interface UseBulkWipActionsOptions {
  wipId: number | string;
  reasonOptions: ReasonOption[];
  setModal: Dispatch<SetStateAction<ModalState>>;
  refreshWip: () => Promise<unknown>;
  clearIncomingDetails: () => void;
  clearOutgoingDetails: () => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  logout: () => void;
}

export default function useBulkWipActions({
  wipId,
  reasonOptions,
  setModal,
  refreshWip,
  clearIncomingDetails,
  clearOutgoingDetails,
  showSuccess,
  showError,
  logout,
}: UseBulkWipActionsOptions) {
  const [incomingReturnItems, setIncomingReturnItems] = useState<SelectedWipItem[]>([]);
  const [outgoingDeleteItems, setOutgoingDeleteItems] = useState<SelectedWipItem[]>([]);

  const closeModal = () => setModal({ open: false, childComponent: null });

  const runBatch = async (
    endpoint: string,
    items: SelectedWipItem[],
    reasonKey: "reason" | "reason_selected",
    reason: ReasonOption,
    successMessage: string,
    failureMessage: string,
    onSuccess: () => void,
  ) => {
    const result = await makePostApiCall(endpoint, {
      wip_id: wipId,
      barcodes: items.map(({ barcode }) => barcode),
      [reasonKey]: reason,
    });

    if (!("success" in result) || !result.success) {
      const errorStatus =
        "errorStatus" in result ? result.errorStatus : result.data?.errorStatus;
      const errorMessage =
        result.data && "message" in result.data
          ? result.data.message
          : failureMessage;

      if (errorStatus === 401) logout();
      showError(errorMessage || failureMessage);
      return result;
    }

    onSuccess();
    closeModal();
    await refreshWip();
    showSuccess(successMessage);

    return result;
  };

  const returnSelectedIncomingItems = (reason: ReasonOption) =>
    runBatch(
      "wipItems/bulkReturn",
      incomingReturnItems,
      "reason_selected",
      reason,
      `${incomingReturnItems.length} incoming item(s) returned`,
      "Could not return the selected items",
      () => {
        setIncomingReturnItems([]);
        clearIncomingDetails();
      },
    );

  const deleteSelectedOutgoingItems = (reason: ReasonOption) =>
    runBatch(
      "wipItems/bulkDeleteOutput",
      outgoingDeleteItems,
      "reason",
      reason,
      `${outgoingDeleteItems.length} outgoing item(s) deleted`,
      "Could not delete the selected items",
      () => {
        setOutgoingDeleteItems([]);
        clearOutgoingDetails();
      },
    );

  const openConfirmation = (
    actionLabel: "Return" | "Delete",
    items: SelectedWipItem[],
    onConfirm: (reason: ReasonOption) => Promise<unknown>,
  ) => {
    if (!items.length) return;

    setModal({
      open: true,
      childComponent: (
        <BulkWipItemsActionForm
          actionLabel={actionLabel}
          count={items.length}
          onConfirm={onConfirm}
          options={reasonOptions}
          setOpen={setModal}
        />
      ),
    });
  };

  return {
    incomingReturnItems,
    setIncomingReturnItems,
    askReturnSelectedIncomingItems: () =>
      openConfirmation("Return", incomingReturnItems, returnSelectedIncomingItems),
    outgoingDeleteItems,
    setOutgoingDeleteItems,
    askDeleteSelectedOutgoingItems: () =>
      openConfirmation("Delete", outgoingDeleteItems, deleteSelectedOutgoingItems),
  };
}
